import {
  ColumnOrderState,
  PaginationState,
  RowModel,
  RowSelectionState,
  SortingState,
  Table,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useHistoryRequestParams, useSingleHistoryMessage } from '../../HistoryStore';
import { useSelectingRow } from 'pages/Message/common/CheckSelectStore';
import CheckSelectTable from 'pages/Message/common/CheckSelectTable';
import { IHistoryTableDataAuto } from './HistoryTable';
import dayjs from 'dayjs';
import { AlertModal } from 'components/Modal/AlertModal';
import styled from 'styled-components';
import { Stack, StackProps } from 'components/Stack';
import { Button } from 'components/Button';
import { Text } from 'components/Typography';
import { useIsDifferenceStore } from 'pages/Message/common/MessagePromptStore';
import { TDragablePickerOptions } from 'components/Popup/DragablePicker';
import TableColumnOrderControlModal from 'components/Modal/TableColumnOrderControlModal';
import theme from 'constants/theme';
import { ReactComponent as IcSetting } from 'constants/icon/ic_setting.svg';
import { IDB_Names } from 'constants/IDB';
import setupIndexedDB, { useIndexedDBStore } from 'hooks/useIndexedDB';

const columnHelper = createColumnHelper<IHistoryTableDataAuto>();

type Props = {
  data: IHistoryTableDataAuto[];
  isFetching: boolean;
};

const setting_HistoryTableComponent_ManualMessage_columns_content: TDragablePickerOptions = [
  // { key: 'sendDate', name: '발송(예정)일/시간', isSelected: true },
  { key: 'title', name: '제목', isSelected: true },
  { key: 'recipant_name', name: '수신인 명', isSelected: true },
  { key: 'recipant_number', name: '수신인 수', isSelected: false },
];
const setting_HistoryTableComponent_columns_content: TDragablePickerOptions = [
  // { key: 'sendDate', name: '발송(예정)일/시간', isSelected: true },
  { key: 'recipentType', name: '수신인 종류', isSelected: true },
  { key: 'title', name: '제목', isSelected: true },
  { key: 'recipant_name', name: '수신인 명', isSelected: true },
  { key: 'recipant_phone', name: '수신인 전화번호', isSelected: false },
  { key: 'exceptionTime', name: '제외 시간', isSelected: false },
];

export default function MessageHistoryTableComponent({ data, isFetching }: Props) {
  const { requestParams } = useHistoryRequestParams();
  const { singleHistoryMessage, setSingleHistoryMessage } = useSingleHistoryMessage();
  const { isDifference, setIsDifference } = useIsDifferenceStore();

  const { selectRow, setselectRow } = useSelectingRow();

  // 조회항목 설정 처리 관련
  const [TableColumnOrderControlModalIsOpen, setTableColumnOrderControlModalIsOpen] = useState<boolean>(false);
  const [tableColumnSettingOptions, setTableColumnSettingOptions] = useState<TDragablePickerOptions>(
    requestParams?.tabType === 'MANUAL_MESSAGE'
      ? setting_HistoryTableComponent_ManualMessage_columns_content
      : setting_HistoryTableComponent_columns_content
  );
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [sorting, setSorting] = useState<SortingState>([]);

  // 조회항목 indexedDB 관련
  const { update, getOneByKey } = useIndexedDBStore(IDB_Names.user.stores[0].name);

  const setColumnOrderInIDB = async (memory: TDragablePickerOptions) => {
    let IDB_CurrentUser = (await getOneByKey('userId', parseInt(window.localStorage.getItem('userId') ?? '-33'))) as {
      [key: string]: any;
    };

    if (!IDB_CurrentUser) {
      return;
    } else {
      // messageHistoryTableColumnsSetting 객체에 memory 객체로 저장
      IDB_CurrentUser.messageHistoryTableColumnsSetting = {
        ...IDB_CurrentUser.messageHistoryTableColumnsSetting,
        [requestParams.tabType]: memory,
      };

      update({ ...IDB_CurrentUser });
    }
  };

  const getIDBColumn = async () => {
    const targetUserId = window.localStorage.getItem('userId');
    if (!targetUserId) return;
    else {
      let res = await (getOneByKey('userId', parseInt(targetUserId)) as any);

      let tableColumnMemory: TDragablePickerOptions;
      switch (requestParams.tabType) {
        case 'MANUAL_MESSAGE':
          tableColumnMemory = setting_HistoryTableComponent_ManualMessage_columns_content;
          break;
        case 'AUTO_MESSAGE':
          tableColumnMemory = setting_HistoryTableComponent_columns_content;
          break;
        case 'EXCEPTION_MESSAGE':
          tableColumnMemory = setting_HistoryTableComponent_columns_content;
          break;
      }

      const updater = (res?.messageHistoryTableColumnsSetting[requestParams.tabType] as TDragablePickerOptions)?.map(
        (d, i) => {
          if (d.name !== tableColumnMemory.find(x => x.key === d.key)?.name) {
            return {
              ...d,
              name: tableColumnMemory.find(x => x.key === d.key)?.name,
            };
          } else {
            return d;
          }
        }
      );

      if (updater) setTableColumnSettingOptions(updater as TDragablePickerOptions);
    }
  };

  useEffect(() => {
    setupIndexedDB({
      databaseName: IDB_Names.user.DB_Name,
      version: IDB_Names.user.version,
      stores: IDB_Names.user.stores,
    })
      .then(() => {
        console.log('successfully set');
      })
      .catch(e => console.error('error / unsupported', e));
  }, []);

  useEffect(() => {
    getIDBColumn();
  }, []);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  // 임시 작성 모달 띄울 때 이전에 선택한 row 정보
  const [snapshotHistoryTableData, setSnapshotHistoryTableData] = useState<IHistoryTableDataAuto | null>(null);
  // 임시 작성 중 취소 모달 상태
  const [isOpenCancelChangeModal, setIsOpenCancelChangeModal] = useState(false);
  // 임시 작성 중 취소 모달 닫기
  const onClickModalWriteCancel = () => {
    // snapshotHistoryTableData 가 있을 때만 row 선택 처리/없을 땐 선택 해제
    if (!!snapshotHistoryTableData) {
      setselectRow(snapshotHistoryTableData.messageId);
      setSingleHistoryMessage(snapshotHistoryTableData);
      // 테이블 행 선택 후 setIsOpenCancelChangeModal(false) 처리
      setIsDifference(false);
      // 기존에 선택했던 snapshotHistoryTableData 초기화
      setSnapshotHistoryTableData(null);
    } else {
      setselectRow('');
      setSingleHistoryMessage(null);
    }
    setIsOpenCancelChangeModal(false);
  };

  // 텝 변경 시 테이블 헤더 변경 처리 함수
  const onChangeTapTableColumn = async () => {
    let tableColumnMemory: TDragablePickerOptions;
    switch (requestParams.tabType) {
      case 'MANUAL_MESSAGE':
        tableColumnMemory = setting_HistoryTableComponent_ManualMessage_columns_content;
        break;
      case 'AUTO_MESSAGE':
        tableColumnMemory = setting_HistoryTableComponent_columns_content;
        break;
      case 'EXCEPTION_MESSAGE':
        tableColumnMemory = setting_HistoryTableComponent_columns_content;
        break;
    }

    const targetUserId = window.localStorage.getItem('userId');

    if (!targetUserId) {
      //@ts-ignore
      return setTableColumnSettingOptions(tableColumnMemory);
    } else {
      let res = await (getOneByKey('userId', parseInt(targetUserId)) as any);
      const updater = (res?.messageHistoryTableColumnsSetting[requestParams.tabType] as TDragablePickerOptions)?.map(
        (d, i) => {
          if (d.name !== tableColumnMemory.find(x => x.key === d.key)?.name) {
            return {
              ...d,
              name: tableColumnMemory.find(x => x.key === d.key)?.name,
            };
          } else {
            return d;
          }
        }
      );

      if (updater) setTableColumnSettingOptions(updater as TDragablePickerOptions);
      else {
        //@ts-ignore
        setTableColumnSettingOptions(tableColumnMemory);
      }
    }
  };

  // 탭 변경 시 설정 모달 컬럼 변경 처리
  useEffect(() => {
    onChangeTapTableColumn();
    // if (requestParams.tabType === 'MANUAL_MESSAGE') {
    //   setTableColumnSettingOptions(setting_HistoryTableComponent_ManualMessage_columns_content);
    // } else {
    //   setTableColumnSettingOptions(setting_HistoryTableComponent_columns_content);
    // }
  }, [requestParams.tabType]);

  // 조회항목 설정 모달에서 컬럼 설정 후 적용
  useEffect(() => {
    let columnVisible: { [key: string]: boolean } = {
      sendDate: true,
    };
    tableColumnSettingOptions.forEach(d => {
      d.hidden ? (columnVisible[`${d.key}`] = false) : (columnVisible[`${d.key}`] = d.isSelected);
    });
    setColumnVisibility(columnVisible);
    setColumnOrder(['sendDate'].concat(tableColumnSettingOptions.map(d => `${d.key}`)));
  }, [tableColumnSettingOptions]);

  const columns = useMemo(() => {
    // 탭 메뉴에 따라 다르게 헤더 설정
    if (requestParams.tabType === 'MANUAL_MESSAGE') {
      return [
        columnHelper.accessor(row => row.sendDate, {
          id: 'sendDate',
          header: '발송(예정)일/시간',
          cell: info => {
            return <span>{dayjs(info.row.original.sendDate).format('YYYY.MM.DD HH:mm')}</span>;
          },
          size: 150,
          footer: info => info.column.id,
        }),
        columnHelper.accessor(row => row.title, {
          id: 'title',
          header: '제목',
          sortingFn: 'alphanumeric',
          size: 740,
          footer: info => info.column.id,
        }),
        columnHelper.accessor(row => row.recipant_name, {
          id: 'recipant_name',
          header: '수신인 명',
          size: 100,
          footer: info => info.column.id,
        }),
        columnHelper.accessor(row => row, {
          id: 'recipant_number',
          header: '수신인 수',
          cell: info => {
            // , 처리 추가
            return info.row.original.recipent_infos.length;
          },
          size: 100,
          footer: info => info.column.id,
        }),
      ];
    } else {
      return [
        columnHelper.accessor(row => row.sendDate, {
          id: 'sendDate',
          header: '발송(예정)일/시간',
          cell: info => {
            return <span>{dayjs(info.row.original.sendDate).format('YYYY.MM.DD HH:mm')}</span>;
          },
          size: 150,
          footer: info => info.column.id,
        }),
        columnHelper.accessor(row => row.recipentType, {
          id: 'recipentType',
          header: '수신인 종류',
          cell: info => {
            const value = info.getValue();
            if (!!value) {
              if (['client', 'driver', 'salesman', 'consignor'].includes(value)) {
                const name = {
                  client: '고객',
                  driver: '기사',
                  salesman: '중개사',
                  consignor: '화주사',
                };
                return name[value as keyof typeof name];
              } else {
                return value;
              }
            } else {
              return info.getValue();
            }
          },
          size: 150,
          footer: info => info.column.id,
        }),
        columnHelper.accessor(row => row.title, {
          id: 'title',
          header: '제목',
          size: 740,
          footer: info => info.column.id,
        }),
        columnHelper.accessor(row => row.recipant_name, {
          id: 'recipant_name',
          header: '수신인 명',
          size: 100,
          footer: info => info.column.id,
        }),
        columnHelper.accessor(row => row.recipent_infos, {
          id: 'recipant_phone',
          header: '수신인 전화번호',
          cell: info => {
            // 전화 번호 형태로 변환처리
            return info.row.original.recipent_infos[0].customer_phone.length > 0
              ? info.row.original.recipent_infos[0].customer_phone.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3')
              : '';
          },
          size: 100,
          footer: info => info.column.id,
        }),
        columnHelper.accessor(row => row, {
          id: 'exceptionTime',
          header: '제외 시간',
          cell: info => {
            if (
              info.row.original.exceptionStartTime &&
              info.row.original.exceptionEndTime &&
              info.row.original.exceptionStartTime.length > 0 &&
              info.row.original.exceptionEndTime.length > 0
            ) {
              return `${dayjs(info.row.original.exceptionStartTime, 'HH:mm:ss').format('HH:mm')} ~ ${dayjs(
                info.row.original.exceptionEndTime,
                'HH:mm:ss'
              ).format('HH:mm')}`;
            } else {
              return '';
            }
          },
          size: 200,
          footer: info => info.column.id,
        }),
      ];
    }
  }, [requestParams]);

  const instance = useReactTable({
    data: data,
    columns: columns,
    state: {
      pagination,
      columnVisibility,
      sorting,
      columnOrder,
    },
    enableRowSelection: true, //enable row selection for all rows
    initialState: {},
    onSortingChange: setSorting,
    onColumnOrderChange: setColumnOrder,
    onColumnVisibilityChange: setColumnVisibility,
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),

    sortDescFirst: true,
    // getRowId: row => row.id.toString(),
  });

  return (
    <>
      <Stack sx={{ marginTop: '10px' }}>
        <CheckSelectTable
          table={instance}
          isDataFetching={isFetching}
          tableType="auto"
          selectRowType="messageId"
          onRowClick={(e, row: { original: IHistoryTableDataAuto }) => {
            // isDiffercen 에 따라 다른 row 클릭 안되게 처리
            if (isDifference) {
              setIsOpenCancelChangeModal(true);
              setSnapshotHistoryTableData(row.original);
            } else {
              setselectRow(row.original.messageId);
              setSingleHistoryMessage(row.original);
            }
          }}
          clearBottom
          {...{ pagination, setPagination }}
          sc={[
            <Button
              type="button"
              variant={'tertiary'}
              w={126}
              h={32}
              icon={[IcSetting, { width: 16, height: 16, fill: theme.colors.RG04 }]}
              onClick={() => setTableColumnOrderControlModalIsOpen(true)}
            >
              조회항목 설정
            </Button>,
          ]}
        />
      </Stack>
      <TableColumnOrderControlModal
        isOpen={TableColumnOrderControlModalIsOpen}
        setIsOpen={setTableColumnOrderControlModalIsOpen}
        options={tableColumnSettingOptions}
        setOptions={setTableColumnSettingOptions}
        initial={
          requestParams?.tabType === 'MANUAL_MESSAGE'
            ? setting_HistoryTableComponent_ManualMessage_columns_content
            : setting_HistoryTableComponent_columns_content
        }
        setColumnOrderInIDB={setColumnOrderInIDB}
      />
      <AlertModal
        type="WARNING"
        isOpen={isOpenCancelChangeModal}
        messages={
          <Stack sx={{ gap: '8px' }}>
            <Text styleName="body2" color="RG02">
              수정을 취소하시겠습니까?
            </Text>
            <Text styleName="body2" color="RG02">
              작성 중이던 내용은 저장되지 않습니다.
            </Text>
          </Stack>
        }
        buttonGroup={
          <ModalButtonDiv>
            <Button
              variant="tertiary"
              h={40}
              sx={{ width: '100%', marginTop: '40px' }}
              onClick={() => setIsOpenCancelChangeModal(false)}
            >
              <Text styleName="body1" color="RG03">
                계속 작성
              </Text>
            </Button>
            <Button
              variant="primary"
              h={40}
              sx={{ width: '100%', marginTop: '40px' }}
              onClick={onClickModalWriteCancel}
            >
              <Text styleName="body1" color="RG00">
                작성 취소
              </Text>
            </Button>
          </ModalButtonDiv>
        }
      />
    </>
  );
}

const ModalButtonDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  align-self: stretch;
`;
